import { useRouteQuery } from '@vueuse/router';
import { MaybeRefOrGetter } from '@vueuse/core';

import { useFeature, Decisions } from '~~core';

import { useRubricate, RubricateSchema } from '../use-rubricate';
import { useFetchRubricates } from '../../repositories/document';

export const useSignaturePositionSign = (signKey: MaybeRefOrGetter<string>, docKey: MaybeRefOrGetter<string>, requirements: MaybeRefOrGetter<RubricateSchema[]>) => {
  const { isEnabled: isTogglePositionSignatureWidgetEnabled } = useFeature(Decisions.SIGNATURE_POSITION_SIGN);

  if (!isTogglePositionSignatureWidgetEnabled.value) return {};

  const {
    rubricateTotal,
    rubricatesInDocument,
    documentsToRubricate,
    documentIndex,
    positionedKinds,
  } = useRubricate(requirements, docKey);

  const isOnlyView = useRouteQuery('is-only-view', undefined, { transform: Boolean });

  const widgetSignatureModal = 'widgetSignatureModal';

  const rubricateRequestType = computed(() => {
    const kinds = positionedKinds.value;

    return kinds.includes('manuscript') && kinds.includes('initials') ? 'both' : kinds[0];
  });

  const { rubricates, refetch: refreshRubricates, isFetching: isLoadingRubricate, isError: isRubricateError } = useFetchRubricates(signKey, rubricateRequestType);

  const isNotExpired = computed(() => {
    const { expireAt } = rubricates.value || {};
    const now = new Date();
    const expireDate = expireAt ? new Date(expireAt) : new Date();

    return now < expireDate;
  });

  const hasAllPositioned = computed(() => toValue(positionedKinds).every((rubricateType) => !!rubricates?.value?.[rubricateType]));

  const overrideExpirationDate = ref(false);

  const areArtifactsValid = computed(() => !isRubricateError.value && hasAllPositioned.value && (isNotExpired.value || overrideExpirationDate.value));

  const artefacts = computed(() => {
    const { manuscript, initials } = rubricates.value || {};

    return {
      manuscript,
      initials,
    };
  });

  const canShowPositioned = computed(() => rubricateTotal.value > 0 && !toValue(isOnlyView));
  const signatureTotal = ref(0);

  return {
    rubricates,
    isRubricateError,
    areArtifactsValid,
    refreshRubricates,
    widgetSignatureModal,
    isLoadingRubricate,
    canShowPositioned,
    rubricateTotal,
    rubricatesInDocument,
    signatureTotal,
    documentsToRubricate,
    documentIndex,
    positionedKinds,
    artefacts,
    overrideExpirationDate,
  };
};
